import { useState, useEffect } from "react";
import { authFetch } from "../api";

const MERGE_FIELDS = [
  { key: "list_price", label: "Price", format: (v) => v ? `$${v.toLocaleString()}` : "-" },
  { key: "listing_status", label: "Status" },
  { key: "total_sqft", label: "Sqft", format: (v, row) => v ? `${v.toLocaleString()}${row.sqft_is_estimated ? " (est)" : ""}` : "-" },
  { key: "dishwasher", label: "Dishwasher", format: (v) => v === true ? "Yes" : v === false ? "No" : "-" },
  { key: "washer_dryer", label: "W/D", format: (v) => v || "-" },
  { key: "private_outdoor_space", label: "Outdoor", format: (v) => v === true ? "Yes" : v === false ? "No" : "-" },
  { key: "outdoor_type", label: "Outdoor Type", format: (v) => v || "-" },
  { key: "ai_renovation", label: "Renovation", format: (v) => v || "-" },
  { key: "ai_floor", label: "Floor", format: (v) => v ?? "-" },
  { key: "monthly_cc", label: "HOA", format: (v) => v ? `$${v.toLocaleString()}` : "-" },
  { key: "monthly_taxes", label: "Tax", format: (v) => v ? `$${v.toLocaleString()}` : "-" },
  { key: "photo_count", label: "Photos", format: (v) => `${v || 0}` },
  { key: "floorplan_count", label: "Floor Plans", format: (v) => `${v || 0}` },
  { key: "se_url", label: "SE URL", format: (v) => v ? "Yes" : "-" },
  { key: "se_scraped_at", label: "SE Scraped", format: (v) => v ? v.slice(0, 10) : "-" },
  { key: "tax_extracted_at", label: "Tax Extracted", format: (v) => v ? v.slice(0, 10) : "-" },
];

const formatDate = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
};

export default function ListingMergeModal({ listingId, onClose, onMerged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(false);
  const [picks, setPicks] = useState({});

  useEffect(() => {
    setData(null);
    setPicks({});
    loadPreview();
  }, [listingId]);

  const loadPreview = async () => {
    setLoading(true);
    setData(null);
    setPicks({});
    try {
      const res = await authFetch(`/listings/${listingId}/merge-preview`);
      setData(res);
      // Default picks: for each field, pick whichever has data (prefer "other" for enrichment data)
      const defaults = {};
      MERGE_FIELDS.forEach((f) => {
        const cVal = res.current?.[f.key];
        const oVal = res.other?.[f.key];
        if (cVal != null && cVal !== "" && cVal !== 0 && cVal !== false) {
          defaults[f.key] = "current";
        } else if (oVal != null && oVal !== "" && oVal !== 0 && oVal !== false) {
          defaults[f.key] = "other";
        } else {
          defaults[f.key] = "current";
        }
      });
      setPicks(defaults);
    } catch (err) {
      console.error("Failed to load merge preview:", err);
    }
    setLoading(false);
  };

  const handleMerge = async () => {
    if (!window.confirm("Merge these listings? The source listing will be marked as DELETED.")) return;
    setMerging(true);
    try {
      const fieldOverrides = {};
      Object.entries(picks).forEach(([field, pick]) => {
        fieldOverrides[field] = pick === "current" ? "new" : "old";
      });
      await authFetch(`/listings/${listingId}/merge`, {
        method: "POST",
        body: JSON.stringify({
          source_listing_id: data.other.id,
          field_overrides: fieldOverrides,
        }),
      });
      if (onMerged) onMerged();
      onClose();
    } catch (err) {
      console.error("Merge failed:", err);
      alert("Merge failed: " + err.message);
    }
    setMerging(false);
  };

  const overlayStyle = {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9998,
    display: "flex", alignItems: "center", justifyContent: "center",
  };

  if (loading) return (
    <div style={overlayStyle}>
      <div style={{ background: "#fff", padding: 32, borderRadius: 8 }}>Loading merge preview...</div>
    </div>
  );

  if (!data) return (
    <div style={overlayStyle}>
      <div style={{ background: "#fff", padding: 32, borderRadius: 8 }}>
        <p>Failed to load merge data</p>
        <button onClick={onClose}>Close</button>
      </div>
    </div>
  );

  const { current, other } = data;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div
        style={{ background: "#fff", borderRadius: 8, maxWidth: 900, width: "95%", maxHeight: "90vh", overflow: "auto", padding: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: "16px 24px", borderBottom: "2px solid #e0e0e0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{current.address} #{current.unit_number}</div>
            <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
              Merge duplicate listings — pick which values to keep
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer" }}>&times;</button>
        </div>

        {/* Listing info header */}
        <div style={{ display: "grid", gridTemplateColumns: "180px 1fr 40px 1fr", gap: 0, padding: "8px 24px", background: "#f5f5f5", borderBottom: "1px solid #e0e0e0", fontSize: 12, fontWeight: 600, color: "#666" }}>
          <div>Field</div>
          <div>
            New (#{current.id}) — will be deleted
            <div style={{ fontWeight: 400, fontSize: 11 }}>
              {current.listing_status} | {formatDate(current.created_date)}
            </div>
          </div>
          <div></div>
          <div>
            Original (#{other.id}) — will be kept
            <div style={{ fontWeight: 400, fontSize: 11 }}>
              {other.listing_status} | {formatDate(other.created_date)}
            </div>
          </div>
        </div>

        {/* Field rows */}
        {MERGE_FIELDS.map((f) => {
          const cVal = current[f.key];
          const oVal = other[f.key];
          const cDisplay = f.format ? f.format(cVal, current) : (cVal ?? "-");
          const oDisplay = f.format ? f.format(oVal, other) : (oVal ?? "-");
          const picked = picks[f.key];
          const isDifferent = String(cVal ?? "") !== String(oVal ?? "");

          return (
            <div
              key={f.key}
              style={{
                display: "grid",
                gridTemplateColumns: "180px 1fr 40px 1fr",
                gap: 0,
                padding: "6px 24px",
                borderBottom: "1px solid #f0f0f0",
                background: isDifferent ? "#fffde7" : "#fff",
                fontSize: 13,
                alignItems: "center",
              }}
            >
              <div style={{ fontWeight: 500, color: "#333" }}>{f.label}</div>
              <div
                onClick={() => setPicks((p) => ({ ...p, [f.key]: "current" }))}
                style={{
                  cursor: "pointer",
                  padding: "4px 8px",
                  borderRadius: 4,
                  background: picked === "current" ? "#e3f2fd" : "transparent",
                  border: picked === "current" ? "2px solid #1976d2" : "2px solid transparent",
                  fontWeight: picked === "current" ? 600 : 400,
                }}
              >
                {String(cDisplay)}
              </div>
              <div style={{ textAlign: "center", color: "#ccc" }}>
                {isDifferent ? "vs" : "="}
              </div>
              <div
                onClick={() => setPicks((p) => ({ ...p, [f.key]: "other" }))}
                style={{
                  cursor: "pointer",
                  padding: "4px 8px",
                  borderRadius: 4,
                  background: picked === "other" ? "#e8f5e9" : "transparent",
                  border: picked === "other" ? "2px solid #4caf50" : "2px solid transparent",
                  fontWeight: picked === "other" ? 600 : 400,
                }}
              >
                {String(oDisplay)}
              </div>
            </div>
          );
        })}

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: "2px solid #e0e0e0", display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <button
            onClick={onClose}
            style={{ padding: "8px 20px", border: "1px solid #ccc", borderRadius: 4, background: "#fff", cursor: "pointer", fontSize: 14 }}
          >
            Cancel
          </button>
          <button
            onClick={handleMerge}
            disabled={merging}
            style={{
              padding: "8px 20px", border: "none", borderRadius: 4,
              background: "#1976d2", color: "#fff", cursor: merging ? "wait" : "pointer",
              fontSize: 14, fontWeight: 600,
            }}
          >
            {merging ? "Merging..." : "Merge & Resolve"}
          </button>
        </div>
      </div>
    </div>
  );
}
